/**
 * Toasts for saves, raised from the query client's MutationCache (main.tsx)
 * so no screen wires them by hand. A mutation declares its success message
 * in `meta.success` (a string, or a function of the answer and the input);
 * without one it confirms nothing. Any failure toasts "Could not save" (or
 * `meta.failure`) with the server's reason (for a 422 naming fields, a pointer to them), unless `meta.quiet` says the
 * screen shows the outcome itself (sign-in, a model's connection test), or `meta.failureShown` says it shows a
 * failure itself while a success still toasts (applying a refinement explains each refusal inline). A 401
 * never toasts: the session has ended and the app returns to sign-in.
 */

import type { Mutation } from '@tanstack/react-query';

import { toast } from '@/ui/Toast';

import { ApiError } from './client';

export interface FeedbackMeta extends Record<string, unknown> {
  success?: string | ((data: never, variables: never) => string);
  failure?: string;
  quiet?: boolean;
  failureShown?: boolean;
}

declare module '@tanstack/react-query' {
  interface Register {
    mutationMeta: FeedbackMeta;
  }
}

type AnyMutation = Mutation<unknown, unknown, unknown, unknown>;

/** The toast's reason: a 422 naming fields is shown beside each field (ApiError.fields), so it only points there. */
function reasonOf(error: unknown): string | undefined {
  if (error instanceof ApiError && error.status === 422 && Object.keys(error.fields).length > 0) {
    return 'Check the highlighted fields.';
  }
  return error instanceof Error ? error.message : undefined;
}

export const notifyMutation = {
  onSuccess: (data: unknown, variables: unknown, _context: unknown, mutation: AnyMutation) => {
    const success = mutation.meta?.success;
    if (!success || mutation.meta?.quiet) return;
    toast.success(typeof success === 'function' ? (success as (d: unknown, v: unknown) => string)(data, variables) : success);
  },
  onError: (error: unknown, _variables: unknown, _context: unknown, mutation: AnyMutation) => {
    if (mutation.meta?.quiet || mutation.meta?.failureShown) return;
    if (error instanceof ApiError && error.status === 401) return;
    toast.error(mutation.meta?.failure ?? 'Could not save', reasonOf(error));
  },
};
